import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, from, map, of, switchMap } from 'rxjs';
import { MetadataService } from '../metadata/metadata.service';
import { CountryService } from '../country/country.service';
import { LocalStorageService } from '../storage/storage.service';
import { CountryId } from 'src/app/types/country.type';
import {
  getDeepLink,
  getLinkRouterUrl,
  LINK_ROUTER_PARAM,
} from 'src/app/components/link-router/link-router.routes';

@Injectable({
  providedIn: 'root',
})
export class ApiMetadataGuard {
  constructor(
    private metadata: MetadataService,
    private countryService: CountryService,
    private router: Router,
    private localStorage: LocalStorageService,
  ) {}

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(route, state);
  }
  canLoad(
    route: Route,
    segments: UrlSegment[],
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const state = {
      url:
        this.router.getCurrentNavigation()?.extractedUrl.toString() ??
        `/${segments.map((segment) => segment.path).join('/')}`,
    } as RouterStateSnapshot;
    return this.canActivate(route as ActivatedRouteSnapshot, state);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<any> | UrlTree {
    const requestedUrl = new URL(
      typeof state?.url === 'string'
        ? state.url
        : `${window.location.pathname}${window.location.search}`,
      window.location.origin,
    );
    // Check if we're accessing policies
    const isPoliciesRoute = requestedUrl.pathname.includes('policies');
    const countryParam = (requestedUrl.searchParams.get('redirectCountry') ||
      (isPoliciesRoute && requestedUrl.searchParams.get('country'))) as CountryId;

    if (!this.countryService.getSelectedCountryId() && countryParam) {
      return from(this.validateAndSetCountry(countryParam)).pipe(
        switchMap((isValid) => {
          if (isValid) {
            // Country is valid, proceed with metadata update
            return this.metadata.updateMetadataFromApi().pipe(map(() => true));
          } else {
            // Invalid country, redirect to country selection
            this.router.navigate(['country-selection']);
            return of(false);
          }
        }),
      );
    }

    if (!this.countryService.getSelectedCountryId()) {
      // Set the current path for redirection later if country isn't set
      const deepLink = requestedUrl.searchParams.get(LINK_ROUTER_PARAM);
      const currentPath =
        requestedUrl.pathname === '/' && deepLink
          ? getLinkRouterUrl(getDeepLink(deepLink))
          : requestedUrl.pathname;
      this.localStorage.setItem(this.localStorage.redirectURL, currentPath);
      this.router.navigate(['country-selection']);
      return of(false);
    }
    return this.metadata.updateMetadataFromApi().pipe(
      map((_) => {
        return of(true);
      }),
    );
  }

  private async validateAndSetCountry(countryId: CountryId): Promise<boolean> {
    try {
      const [appOnlyCountries, supportedCountries] = await Promise.all([
        this.countryService.loadAppOnlyCountries().toPromise(),
        this.countryService.loadSupportedCountries().toPromise(),
      ]);

      if (appOnlyCountries.includes(countryId) || supportedCountries.includes(countryId)) {
        this.countryService.setSelectedCountryId(countryId);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
}
